import { Outlet } from 'react-router-dom';
import ETLGuard from '../guards/ETLGuard';
import SubscriptionGuard from '../guards/SubscriptionGuard';
import SessionTimeout from '../components/sessionTimeOut/SessionTimeout';
import SubscriptionLockScreen from '../components/SubscriptionLockScreen';
import { useSubscriptionLock } from '../hooks/useSubscriptionLock';
import { APP_FEATURES } from '../constants';
import { hasPrivilege } from '../utility/hasPrivilege';

/**
 * Applies the subscription lockout (CVA-4668) to chromeless routes, the same
 * way MainLayout does: hold the page until the answer is known, then show the
 * lock screen instead of it. Without this, a student opening an exam link for a
 * locked school got a page full of failing calls instead of an explanation.
 */
const ChromelessLockGate = () => {
  const subscriptionLock = useSubscriptionLock();

  if (!subscriptionLock.resolved) {
    return (
      <div
        data-testid="subscription-lock-pending"
        className="flex min-h-dvh items-center justify-center"
      >
        <i className="pi pi-spin pi-spinner text-3xl text-gray-400" aria-hidden="true" />
      </div>
    );
  }

  if (subscriptionLock.locked) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <SubscriptionLockScreen
          code={subscriptionLock.code}
          message={subscriptionLock.message}
          details={subscriptionLock.details}
          canManageBilling={hasPrivilege(APP_FEATURES.BILLING_MANAGEMENT)}
        />
      </div>
    );
  }

  return <Outlet />;
};

/**
 * CVA-4197 join-exam plan 0004 (<<chromeless>>). A student sitting an exam
 * must not be one click from navigating away mid-paper, so this route group
 * has no sidebar and no navbar — but it is still an AUTHENTICATED route and
 * must keep every auth-adjacent guard `MainLayout`'s group applies.
 *
 * No authenticated route escaped `MainLayout` before this: the router had
 * exactly two top-level groups (`PublicLayout`, unauthenticated, and the
 * `MainLayout` group, which renders chrome unconditionally). Rather than
 * special-case `MainLayout` to sometimes skip its own chrome, this is a
 * THIRD top-level group in `AppRouter.lazy.tsx`'s `createBrowserRouter`
 * array, re-wrapping the same guard stack `MainLayout`'s group uses
 * (`ETLGuard` -> `SubscriptionGuard` -> `SessionTimeout`) minus `MainLayout`
 * itself — so the auth semantics stay identical and explicit rather than
 * implied.
 */
const ChromelessLayout = () => {
  return (
    <ETLGuard>
      <SubscriptionGuard>
        <SessionTimeout>
          <div className="min-h-dvh bg-gray-50">
            <ChromelessLockGate />
          </div>
        </SessionTimeout>
      </SubscriptionGuard>
    </ETLGuard>
  );
};

export default ChromelessLayout;
